import "#vendor/cropper.js";
import "#vendor/humps.js";
import "./asset_cropper/safe_frame.js";

window.Sparkle ??= {};

// VITE_MIGRATION: preserve sub-properties attached by glob-imported children
// (e.g. Sparkle.AssetCropper.SafeFrame from ./asset_cropper/safe_frame.js).
// Under ES modules those child modules run BEFORE this file's body, and the
// class assignment below would otherwise overwrite them.
const _previousAssetCropper = window.Sparkle.AssetCropper;

Sparkle.AssetCropper = class AssetCropper {
  constructor(imageElement, cropSettings, options) {
    this._handleCrop = this._handleCrop.bind(this);
    this._handleReady = this._handleReady.bind(this);
    this.zoom = this.zoom.bind(this);
    this.resize = this.resize.bind(this);
    this.getPreviewUrl = this.getPreviewUrl.bind(this);
    this.destroy = this.destroy.bind(this);
    this._renderSafeFrames = this._renderSafeFrames.bind(this);
    this._updateSafeFrames = this._updateSafeFrames.bind(this);
    this._queueUntilReady = this._queueUntilReady.bind(this);
    this._executeQueuedCommands = this._executeQueuedCommands.bind(this);
    this._cropperjsOptions = this._cropperjsOptions.bind(this);
    this._exportCropSettings = this._exportCropSettings.bind(this);
    this.imageElement = imageElement;
    this.options = options;
    this.commandQueue = [];
    Sparkle.Codeload.import(window.SparkleConfig.assetPaths.cropperCss, { type: "css" }).then(
      () => (this.cropper = new Cropper(this.imageElement, this._cropperjsOptions(cropSettings))),
    );

    this.safeFrame1x1 = new Sparkle.AssetCropper.SafeFrame(1, "1x1", { color: "#ded58a", textColor: "#a0a01d" });
    this.safeFrame16x9 = new Sparkle.AssetCropper.SafeFrame(16 / 9, "16x9", { color: "#e91e63", textColor: "#e91e63" });
    this.safeFrame4x3 = new Sparkle.AssetCropper.SafeFrame(4 / 3, "4x3", { color: "#009688", textColor: "#009688" });
  }

  // EVENT HANDLERS

  _handleCrop(event) {
    this._updateSafeFrames();
    return this.options.onCrop(this._exportCropSettings(event.detail));
  }

  _handleReady() {
    this._renderSafeFrames();
    this.ready = true;
    if (this.options.onReady) {
      this.options.onReady();
    }
    return this._executeQueuedCommands();
  }

  // HELPER FUNCTIONS

  zoom(zoomAmount) {
    return this._queueUntilReady(() => this.cropper.zoomTo(zoomAmount));
  }

  resize() {
    return this._queueUntilReady(() => this.cropper.render());
  }

  getPreviewUrl() {
    if (!this.ready) {
      console.error("Cropper not ready yet");
    }
    const canvas = this.cropper.getCroppedCanvas();
    if (canvas != null) {
      return canvas.toDataURL();
    }
  }

  destroy() {
    return this._queueUntilReady(() => {
      this.ready = false;
      return this.cropper.destroy();
    });
  }

  // PRIVATE

  _renderSafeFrames() {
    const cropboxElement = jQuery(this.imageElement).siblings().find(".cropper-crop-box");
    cropboxElement.append(this.safeFrame16x9.el);
    cropboxElement.append(this.safeFrame4x3.el);
    cropboxElement.append(this.safeFrame1x1.el);
    return this._updateSafeFrames();
  }

  _updateSafeFrames() {
    this.safeFrame1x1.render();
    this.safeFrame16x9.render();
    return this.safeFrame4x3.render();
  }

  _queueUntilReady(fn) {
    if (this.ready) {
      return fn();
    }
    return this.commandQueue.push(fn);
  }

  _executeQueuedCommands() {
    return jQuery.each(this.commandQueue, (_, fn) => fn());
  }

  _cropperjsOptions(cropSettings) {
    let opts = {
      guides: false, // Hide the original cropper lines because we are making our own
      autoCrop: true, // Apply crop settings on initialize
      autoCropArea: 1, // Make the crop as large as possible to begin with
      data: this._fitToAspectRatio(this._importCropSettings(cropSettings)),
      dragMode: "move",
      viewMode: 2, // Constrain image to container. If the image extends the conatiner (viewMode 3), then we need to keep track of the canvas position.
      rotatable: false,
      zoomOnWheel: false,
      toggleDragModeOnDblclick: false,
      crop: this._handleCrop,
      ready: this._handleReady,
      aspectRatio: this.options.aspectRatio,
    };

    // Prevent cropperjs from centering the crop box when generating an image while the editor is not visible
    // See https://github.com/fengyuanchen/cropperjs/blob/8fa586fd931fee9aca05451e646270bef3f6cbaf/dist/cropper.js#L1169
    // See https://github.com/fengyuanchen/cropperjs/blob/8fa586fd931fee9aca05451e646270bef3f6cbaf/dist/cropper.js#L1212
    if (opts.aspectRatio != null) {
      opts = {
        minContainerWidth: 100,
        minContainerHeight: 100 * opts.aspectRatio,
        ...opts,
      };
    }

    return opts;
  }

  // NOTE: Accepts camelCase and snake_case
  _importCropSettings(settings) {
    settings = {
      ...settings,
    };
    return {
      width: settings.width,
      height: settings.height,
      x: settings.x_offset != null ? settings.x_offset : settings.xOffset,
      y: settings.y_offset != null ? settings.y_offset : settings.yOffset,
      maxWidth: settings.max_width != null ? settings.max_width : settings.maxWidth,
      maxHeight: settings.max_height != null ? settings.max_height : settings.maxHeight,
    };
  }

  // Returns the largest box of the locked aspect ratio centred in `data`, which is the area the
  // server cuts for a size with that aspect. Returns `data` unchanged when the
  // `fitCropToAspectRatio` option is off, the ratio is not locked, or `data` has no position.
  // Cropper.js fits a box to the ratio by keeping its top-left corner and width.
  _fitToAspectRatio(data) {
    const aspectRatio = this.options.aspectRatio;
    const positioned = [data.x, data.y, data.width, data.height].every((value) => typeof value === "number");

    if (!this.options.fitCropToAspectRatio || !aspectRatio || !positioned) {
      return data;
    }

    const width = Math.min(data.width, data.height * aspectRatio);
    const height = width / aspectRatio;

    return {
      ...data,
      x: data.x + (data.width - width) / 2,
      y: data.y + (data.height - height) / 2,
      width,
      height,
    };
  }

  // NOTE: Exports snake_case
  _exportCropSettings(settings) {
    settings = {
      ...settings,
    };
    return {
      width: this._round(settings.width),
      height: this._round(settings.height),
      x_offset: this._round(settings.x),
      y_offset: this._round(settings.y),
      max_width: this._round(settings.maxWidth),
      max_height: this._round(settings.maxHeight),
    };
  }

  _round(value) {
    if (value != null) {
      return Math.round(value);
    }
  }
};

if (_previousAssetCropper) {
  Object.assign(Sparkle.AssetCropper, _previousAssetCropper);
}
